import { useState, useEffect } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import defaultTo from 'lodash/defaultTo';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { fetchEntitlementTotalCount } from '../queries/fetch-entitlement-total-count';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';

interface Config {
  singleBatchCount: number;
  autoFetch?: boolean;
  skip?: boolean;
  totalCountQuery?: any;
  dataPath: string;
}

const DEFAULT_SINGLE_BATCH_ENTITLEMENT_COUNT = 100;
const graphqlClient = abgGraphqlClient(ProxyGraphClients.ANS);
export const useLoadEntitlementList = (
  query: any,
  variables: any,
  config: Config = {
    dataPath: 'transactionAccount.productSubscriptionsInPPC',
    singleBatchCount: DEFAULT_SINGLE_BATCH_ENTITLEMENT_COUNT,
    autoFetch: false,
    totalCountQuery: fetchEntitlementTotalCount,
  }
) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (config.autoFetch && !config.skip) {
      loadEntitlementList();
    }
  }, [config.skip]);

  const SingleBatchCount = config.singleBatchCount;
  const fetchEntitlementTotalCountData = () => {
    return graphqlClient.query<
      generatedTypesNew.FetchEntitlementTotalCountQuery,
      generatedTypesNew.FetchEntitlementTotalCountQueryVariables
    >({
      query: config.totalCountQuery,
      variables: variables,
      fetchPolicy: 'no-cache',
    });
  };

  const fetchEntitlementList = (offset = 0, first = 100) => {
    // FIXME :: take the types from outside as generic variables
    const sortArg = variables?.searchOptions?.sort ? { sort: variables.searchOptions.sort } : {};
    return graphqlClient.query<
      generatedTypesNew.FetchEntitlementListForDownloadCsvQuery,
      generatedTypesNew.FetchEntitlementListForDownloadCsvQueryVariables
    >({
      query: query,
      variables: {
        ...variables,
        searchOptions: {
          first: first,
          offset: offset,
          ...sortArg,
        },
      },
      fetchPolicy: 'no-cache',
    });
  };

  const fetchEntitlementListDataInChunks = async () => {
    const { data: entitlementTotalCountResponse } = await fetchEntitlementTotalCountData();
    const totalCount = defaultTo(get(entitlementTotalCountResponse, `${config.dataPath}.totalCount`), 0);
    const nodePromises = [];
    for (let i = 0; i < Math.ceil(totalCount / SingleBatchCount); i++) {
      nodePromises.push(fetchEntitlementList(i * SingleBatchCount, SingleBatchCount));
    }
    const allNodes = await Promise.allSettled(nodePromises);
    return allNodes.reduce((allNodes: any[], response) => {
      const nodes = defaultTo(get(response, `value.data.${config.dataPath}.nodes`), []);
      if (response.status === 'fulfilled' && nodes && nodes.length) {
        allNodes = allNodes.concat(nodes);
      }
      return allNodes;
    }, []);
  };

  const loadEntitlementList = async () => {
    try {
      setLoading(true);
      const response = await fetchEntitlementListDataInChunks();
      setLoading(false);
      setData(
        set({}, config.dataPath, {
          totalCount: response.length,
          nodes: response,
        })
      );
      return response;
    } catch (e) {
      setLoading(false);
      setData(null);
      setError(e);
      throw e;
    }
  };

  return {
    isLoading,
    error,
    data,
    loadEntitlementList,
    refetch: loadEntitlementList,
  };
};
